import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Theme configuration
export const THEME_MODES = {
  'light': { code: 'light', name: 'Light' },
  'dark': { code: 'dark', name: 'Dark' },
  'system': { code: 'system', name: 'System' },
} as const;

export const COLOR_THEMES = {
  'yellow': { code: 'yellow', name: 'Yellow' },
  'green': { code: 'green', name: 'Green' },
  'blue': { code: 'blue', name: 'Blue' },
  'purple': { code: 'purple', name: 'Purple' },
} as const;

export type ThemeMode = keyof typeof THEME_MODES;
export type ColorTheme = keyof typeof COLOR_THEMES;

export interface ThemeState {
  // Current theme settings
  mode: ThemeMode;
  colorTheme: ColorTheme;
  
  // Loading states
  isLoadingTheme: boolean;
  
  // Error handling
  themeError: string | null;
  
  // Initialization tracking
  isThemeInitialized: boolean;
  
  // Available options
  availableModes: typeof THEME_MODES;
  availableColorThemes: typeof COLOR_THEMES;
}

// Simple default values - no localStorage loading to avoid SSR issues
const initialState: ThemeState = {
  mode: 'system',
  colorTheme: 'purple',
  isLoadingTheme: false,
  themeError: null,
  isThemeInitialized: false,
  availableModes: THEME_MODES,
  availableColorThemes: COLOR_THEMES,
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    // Set theme mode
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.mode = action.payload;
      state.themeError = null;
    },
    
    // Set color theme
    setColorTheme: (state, action: PayloadAction<ColorTheme>) => {
      state.colorTheme = action.payload;
      state.themeError = null;
    },
    
    // Set both at once
    setTheme: (state, action: PayloadAction<{ mode: ThemeMode; colorTheme: ColorTheme }>) => {
      state.mode = action.payload.mode;
      state.colorTheme = action.payload.colorTheme;
      state.themeError = null;
    },
    
    // Toggle between light and dark
    toggleTheme: (state) => {
      state.mode = state.mode === 'light' ? 'dark' : 'light';
      state.themeError = null;
    },
    
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoadingTheme = action.payload;
      if (action.payload) {
        state.themeError = null;
      }
    },
    
    startLoadingTheme: (state) => {
      state.isLoadingTheme = true;
      state.themeError = null;
    },
    
    completeLoadingTheme: (state, action: PayloadAction<{ mode: ThemeMode; colorTheme: ColorTheme }>) => {
      state.isLoadingTheme = false;
      state.mode = action.payload.mode;
      state.colorTheme = action.payload.colorTheme;
      state.themeError = null;
      state.isThemeInitialized = true;
    },
    
    failLoadingTheme: (state, action: PayloadAction<string>) => {
      state.isLoadingTheme = false;
      state.themeError = action.payload;
    },
    
    // Error handling
    clearThemeError: (state) => {
      state.themeError = null;
    },
    
    // Reset theme to defaults
    resetTheme: (state) => {
      state.mode = 'system';
      state.colorTheme = 'purple';
      state.isLoadingTheme = false;
      state.themeError = null;
      state.isThemeInitialized = false;
    },
    
    // Mark as initialized
    markThemeInitialized: (state) => {
      state.isThemeInitialized = true;
    },
  },
});

export const {
  setThemeMode,
  setColorTheme,
  setTheme,
  toggleTheme,
  setLoading,
  startLoadingTheme,
  completeLoadingTheme,
  failLoadingTheme,
  clearThemeError,
  resetTheme,
  markThemeInitialized,
} = themeSlice.actions;

export default themeSlice.reducer;

// Selectors
export const selectTheme = (state: { theme: ThemeState }) => state.theme;
export const selectCurrentThemeMode = (state: { theme: ThemeState }) => state.theme.mode;
export const selectCurrentColorTheme = (state: { theme: ThemeState }) => state.theme.colorTheme;
export const selectIsLoadingTheme = (state: { theme: ThemeState }) => state.theme.isLoadingTheme;
export const selectThemeError = (state: { theme: ThemeState }) => state.theme.themeError;
export const selectIsThemeInitialized = (state: { theme: ThemeState }) => state.theme.isThemeInitialized;
export const selectAvailableModes = (state: { theme: ThemeState }) => state.theme.availableModes;
export const selectAvailableColorThemes = (state: { theme: ThemeState }) => state.theme.availableColorThemes;

// Computed selectors
export const selectCurrentThemeModeName = (state: { theme: ThemeState }) => {
  const mode = state.theme.mode;
  return THEME_MODES[mode].name;
};

export const selectCurrentColorThemeName = (state: { theme: ThemeState }) => {
  const colorTheme = state.theme.colorTheme;
  return COLOR_THEMES[colorTheme].name;
};